import { useEffect, useState } from 'react';
import { apiGet, apiPut } from '../../services/apiClient';

const defaultSettings = {
  max_adults: 16,
  max_children: 10,
  max_total_guests: 20,
  allow_pets: true,
  allow_infants: true,
  min_nights: 1,
  featured_location_ids: [],
};

const AdminSearchSettings = ({ token }) => {
  const [form, setForm] = useState(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/admin/search-settings', token);
      const data = res?.data || res || {};
      setForm({
        max_adults: Number(data.max_adults ?? defaultSettings.max_adults),
        max_children: Number(data.max_children ?? defaultSettings.max_children),
        max_total_guests: Number(data.max_total_guests ?? defaultSettings.max_total_guests),
        allow_pets: data.allow_pets !== false,
        allow_infants: data.allow_infants !== false,
        min_nights: Number(data.min_nights ?? defaultSettings.min_nights),
        featured_location_ids: Array.isArray(data.featured_location_ids)
          ? data.featured_location_ids
          : [],
      });
    } catch (err) {
      setError(err.message || 'Failed to load search settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setStatus('');
    setError('');
    try {
      const payload = {
        ...form,
        max_adults: Number(form.max_adults || 0),
        max_children: Number(form.max_children || 0),
        max_total_guests: Number(form.max_total_guests || 0),
        min_nights: Number(form.min_nights || 1),
        featured_location_ids: Array.isArray(form.featured_location_ids)
          ? form.featured_location_ids
          : [],
      };
      await apiPut('/api/admin/search-settings', payload, token);
      setStatus('Search settings updated.');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update search settings');
    } finally {
      setSaving(false);
    }
  };

  const updateFeatured = (value) => {
    const list = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));
    setForm((prev) => ({ ...prev, featured_location_ids: list }));
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Settings</p>
        <h1 className="admin-page-title text-xl font-semibold text-slate-900">Search Configuration</h1>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {status && <div className="alert alert-success">{status}</div>}

      <div className="admin-card bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        {loading ? (
          <div className="text-muted">Loading settings...</div>
        ) : (
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label text-sm">Max adults</label>
              <input
                type="number"
                className="form-control"
                value={form.max_adults}
                onChange={(e) => setForm((prev) => ({ ...prev, max_adults: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label text-sm">Max children</label>
              <input
                type="number"
                className="form-control"
                value={form.max_children}
                onChange={(e) => setForm((prev) => ({ ...prev, max_children: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label text-sm">Max total guests</label>
              <input
                type="number"
                className="form-control"
                value={form.max_total_guests}
                onChange={(e) => setForm((prev) => ({ ...prev, max_total_guests: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label text-sm">Min nights</label>
              <input
                type="number"
                className="form-control"
                value={form.min_nights}
                onChange={(e) => setForm((prev) => ({ ...prev, min_nights: e.target.value }))}
              />
            </div>
            <div className="col-md-4 d-flex align-items-center gap-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="allow-pets"
                checked={form.allow_pets}
                onChange={(e) => setForm((prev) => ({ ...prev, allow_pets: e.target.checked }))}
              />
              <label htmlFor="allow-pets" className="form-check-label text-sm">
                Allow pets
              </label>
            </div>
            <div className="col-md-4 d-flex align-items-center gap-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="allow-infants"
                checked={form.allow_infants}
                onChange={(e) => setForm((prev) => ({ ...prev, allow_infants: e.target.checked }))}
              />
              <label htmlFor="allow-infants" className="form-check-label text-sm">
                Allow infants
              </label>
            </div>
            <div className="col-12">
              <label className="form-label text-sm">Featured location IDs (comma separated)</label>
              <input
                className="form-control"
                value={form.featured_location_ids.join(', ')}
                onChange={(e) => updateFeatured(e.target.value)}
                placeholder="1, 2, 3"
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <button className="btn btn-primary" disabled={saving} onClick={save}>
                {saving ? 'Saving...' : 'Save settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSearchSettings;
